import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="border-b border-[#D1D5DB] shv-grain">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-24">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-4">About Safe Harbor Veterans</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-[#0A192F] leading-[1.05]">
            A housing placement and <span className="text-[#1E3A8A]">referral network</span>.
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
            Safe Harbor Veterans is not a housing provider. We are a placement and referral platform connecting housing seekers, housing providers, and referral professionals through a centralized system — built specifically for veterans, returning citizens, individuals in recovery, and families in crisis.
          </p>
        </div>
      </section>

      <section className="bg-white border-b border-[#D1D5DB]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-20 space-y-16">
          <Block kicker="Our Mission" title="Empty beds. Full purpose.">
            Every day a supportive housing bed sits empty, a person who could occupy it is waiting elsewhere. Safe Harbor exists to close that gap — providing real-time vacancy data, verified providers, and a referral workflow that case managers and agencies actually use.
          </Block>

          <Block kicker="Who We Serve" title="Built for the entire placement chain.">
            <ul className="space-y-2 list-disc list-inside text-[#4A5568]">
              <li>Veterans returning to civilian life</li>
              <li>Returning citizens reintegrating after incarceration</li>
              <li>Individuals experiencing homelessness</li>
              <li>Families in housing crisis</li>
              <li>Transitional housing programs, recovery and sober living residences</li>
              <li>Independent Living Facilities (ILFs), shelters, and veteran housing</li>
              <li>Social workers, case managers, VSOs, probation and parole officers</li>
            </ul>
          </Block>

          <Block kicker="What Makes Us Different" title="Not a directory. A referral engine.">
            <div className="space-y-3 text-[#4A5568]">
              <p>Traffic doesn't fill beds. Qualified, verified referrals do. We've built a complete workflow — intake verification, applicant matching, status tracking from Submitted to Completed — that providers actually pay for because it moves people in.</p>
              <p>Seekers always pay $0. Revenue comes from providers who save money filling beds, agencies that scale placements, and premium services like Verified Applicant packages and Successful Placement Fees.</p>
            </div>
          </Block>

          <Block kicker="Where We're Going" title="Starting in North Carolina. Built for the nation.">
            <div className="space-y-3 text-[#4A5568]">
              <p>Phase 1: Housing providers, veteran organizations, shelters, recovery programs, and reentry agencies across North Carolina.</p>
              <p>Phase 2: Expansion across the Southeastern United States.</p>
              <p>Phase 3: A national housing placement and referral network for veterans, returning citizens, families, and individuals experiencing housing instability.</p>
            </div>
          </Block>

          <div className="bg-[#0A192F] text-white p-10 rounded-sm">
            <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-3">Crisis Resources</div>
            <h3 className="font-heading text-2xl font-bold">If you are in crisis right now, call.</h3>
            <ul className="mt-5 space-y-2 text-white/80">
              <li><strong>Veterans Crisis Line:</strong> 988, then press 1</li>
              <li><strong>National Call Center for Homeless Veterans:</strong> 1-877-424-3838</li>
              <li><strong>SAMHSA National Helpline:</strong> 1-800-662-4357</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Join the network.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register?role=seeker"><Button variant="outline" className="border-[#0A192F] text-[#0A192F] px-6">I need housing</Button></Link>
            <Link to="/register?role=provider"><Button className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white px-6">I have beds</Button></Link>
            <Link to="/register?role=case_manager"><Button className="bg-[#C05621] hover:bg-[#9C4221] text-white px-6">I refer clients</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const Block = ({ kicker, title, children }) => (
  <div>
    <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-2">{kicker}</div>
    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F] mb-4">{title}</h2>
    {typeof children === "string" ? <p className="text-[#4A5568] leading-relaxed">{children}</p> : children}
  </div>
);
